/**
 * session.ts — 계정·기기·세션·접속 기록 공용 정의 (순수 도메인, firebase 의존 없음).
 *
 * 설계: docs/design/12_통합관리_계정기기설계.md (§4 데이터 구조 · §7 세션 정책).
 * 2단계 4묶음(A 백엔드 · B 내 계정 · C 통합관리 · D 환경설정)이 함께 쓰는 약속이다.
 *
 * ⚠️ 목록을 바꾸면 함께 갱신:
 *   - ACCESS_LOG_SELF_TYPES → firestore.rules `accessLogSelfTypes()`
 *   - ACCESS_LOG_TYPES      → backend/functions 접속 기록 쓰기
 *   - PUSH_TYPE_SCOPE       → backend/functions `PUSH_TYPE_SCOPE`(collectRecipientTokens)
 */
import type { BizKey, TenantKey } from "./space";
import type { PushNotifyType } from "./push_copy";

// ── 정책 상수 (04 세션 정책 + 12 결정) ─────────────────────────
/** 한 사람이 등록할 수 있는 기기 수(PC·MO 합산). 전체 admin 은 제한 없음 */
export const MAX_DEVICES = 3;
/** PC 유휴 자동 로그아웃 */
export const IDLE_LOGOUT_MS = 4 * 60 * 60 * 1000;
/** 유휴 로그아웃 전 경고 */
export const IDLE_WARNING_MS = 5 * 60 * 1000;
/** 이 기간 쓰지 않으면 다음 진입 때 로그아웃 */
export const INACTIVE_EXPIRE_DAYS = 60;
/** 접속 기록 저장 기간(TTL expire_at) */
export const ACCESS_LOG_RETENTION_YEARS = 5;
/** 접속 기록 화면 기본 조회 기간 */
export const ACCESS_LOG_DEFAULT_VIEW_DAYS = 365;
/** 기기 last_seen_at 중복 갱신 억제 */
export const DEVICE_SEEN_THROTTLE_MS = 15 * 60 * 1000;

/** localStorage 키 */
export const DEVICE_ID_STORAGE_KEY = "rmc_device_id";
export const LAST_ACTIVITY_STORAGE_KEY = "rmc_last_activity_at";

// ── 기기 ──────────────────────────────────────────────────────
export type DeviceKind = "pc" | "mo";
export type DeviceStatus = "active" | "revoked";

/** users/{uid}/devices/{deviceId} */
export interface DeviceDoc {
  id: string;
  name: string;
  browser: string;
  os: string;
  kind: DeviceKind;
  created_at: Date | null;
  last_seen_at: Date | null;
  auto_login: boolean;
  shared: boolean;
  status: DeviceStatus;
  revoke_requested_at: Date | null;
  revoked_by: string | null;
  /** 함수가 전 기기 플래그를 찍은 사유(로그인 화면 안내 문구용). 없으면 null */
  revoke_reason: DeviceRevokeReason | null;
  push_token: string | null;
  session_id: string;
}

/** 기기 플래그 사유 — backend/functions/src/accountOps.ts DEVICE_REVOKE_REASONS 와 같게(런타임 화이트리스트) */
export const DEVICE_REVOKE_REASONS = ["password_changed", "logout_all", "all_devices_logout", "account_suspended", "password_reset"] as const;
export type DeviceRevokeReason = (typeof DEVICE_REVOKE_REASONS)[number];
export function isDeviceRevokeReason(v: unknown): v is DeviceRevokeReason {
  return typeof v === "string" && (DEVICE_REVOKE_REASONS as readonly string[]).includes(v);
}

/** tenants/{t}/members/{uid}.devices[] — 법인 관리자용 요약 사본(함수가 씀) */
export interface MemberDeviceSummary {
  id: string;
  name: string;
  kind: DeviceKind;
  browser: string;
  os: string;
  last_seen_at: Date | null;
  auto_login: boolean;
}

// ── 접속 기록 ─────────────────────────────────────────────────
/** 본인 기기에서 일어나 클라이언트가 직접 쓰는 종류(rules 화이트리스트와 같음) */
export const ACCESS_LOG_SELF_TYPES = [
  "login",
  "auto_login",
  "logout",
  "idle_logout",
  "expired",
  "device_register",
  "device_release",
  "device_rename",
  "auto_login_off",
] as const;

/** 관리자·함수만 쓰는 종류 */
export const ACCESS_LOG_SERVER_TYPES = [
  "forced_logout",
  "all_devices_logout",
  "password_changed",
  "password_reset",
  "account_suspended",
  "account_reactivated",
  "account_created",
  "account_deleted",
  "membership_added",
  "membership_ended",
] as const;

export const ACCESS_LOG_TYPES = [...ACCESS_LOG_SELF_TYPES, ...ACCESS_LOG_SERVER_TYPES] as const;
export type AccessLogSelfType = (typeof ACCESS_LOG_SELF_TYPES)[number];
export type AccessLogType = (typeof ACCESS_LOG_TYPES)[number];

export function isAccessLogType(v: unknown): v is AccessLogType {
  return typeof v === "string" && (ACCESS_LOG_TYPES as readonly string[]).includes(v);
}

export const ACCESS_LOG_TYPE_LABEL: Record<AccessLogType, string> = {
  login: "로그인",
  auto_login: "자동 로그인",
  logout: "로그아웃",
  idle_logout: "유휴 로그아웃",
  expired: "장기 미사용 로그아웃",
  device_register: "기기 등록",
  device_release: "기기 해제",
  device_rename: "기기 이름 변경",
  auto_login_off: "자동 로그인 해제",
  forced_logout: "강제 로그아웃",
  all_devices_logout: "모든 기기 로그아웃",
  password_changed: "비밀번호 변경",
  password_reset: "비밀번호 초기화",
  account_suspended: "계정 정지",
  account_reactivated: "계정 다시 활성",
  account_created: "계정 생성",
  account_deleted: "계정 삭제",
  membership_added: "소속 추가",
  membership_ended: "소속 종료",
};

/** access_log/{autoId} */
export interface AccessLogEntry {
  id: string;
  uid: string;
  login_id: string;
  name: string;
  type: AccessLogType;
  device_id: string | null;
  device_name: string | null;
  browser: string | null;
  os: string | null;
  kind: DeviceKind | null;
  shared_pc: boolean;
  by_uid: string | null;
  by_name: string | null;
  tenants: TenantKey[];
  created_at: Date | null;
  expire_at: Date | null;
}

// ── 본인 요청(account_ops) ────────────────────────────────────
export type AccountOpAction = "self.logout_all" | "self.password_changed" | "self.device_revoke";

// ── 알림 범위(사업부별 on/off) ────────────────────────────────
/** 그룹 종류 = 사업부와 무관 / 사업부 종류 = 어느 사업부 일인지 있음 */
export type PushTypeScope = "group" | "unit";
export const PUSH_TYPE_SCOPE: Record<PushNotifyType, PushTypeScope> = {
  morning_plan: "unit",
  ship_start: "unit",
  ship_finish: "unit",
  ship_eod: "unit",
  signup: "group",
  sales_sync: "unit",
  rqc_drift: "unit",
};

/** preferences/push.scopes 의 키 — homeBoardKey 와 같은 `{법인}_{사업}` */
export function pushScopeKey(t: TenantKey, b: BizKey): string {
  return `${t}_${b}`;
}

/** tenants/{t}/units/{biz}/settings/notify_policy */
export interface NotifyPolicy {
  kinds: Partial<Record<PushNotifyType, boolean>>;
}
